import styled, { css } from 'styled-components'
import { cardisTheme } from '../../theme/cardisTheme'
import {
  ContentArea as SharedContentArea,
  ExpandablePageContainer,
  FooterWrapper as SharedFooterWrapper,
} from '../CardisPropertyCharacteristics/CardisPropertyCharacteristics.styles'

// Re-export shared styles from CardisPropertyCharacteristics
export {
  PageContainer,
  ContentArea,
  Title,
  SectionHeader,
  FooterWrapper,
  FooterPattern,
  Footer,
  PageNumber,
} from '../CardisPropertyCharacteristics/CardisPropertyCharacteristics.styles'

export const TechniquePageContainer = styled(ExpandablePageContainer)`
  min-height: 311mm;
  height: auto;

  @media print {
    min-height: 297mm !important;
    height: auto !important;
    max-height: none !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
    overflow: visible !important;
  }
`

export const TechniqueContentArea = styled(SharedContentArea)`
  flex: none;
`

const printStyles = css`
  @media print {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Section wrapper for spacing
export const SectionWrapper = styled.div`
  margin-bottom: 20px;

  @media print {
    margin-bottom: 20px !important;
  }
`

// VALEUR TECHNIQUE - Full width 4-column table
export const TechniqueTable = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  @media print {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
  }
`

export const TechniqueRowWrapper = styled.div<{ $isHidden?: boolean }>`
  position: relative;

  ${({ $isHidden }) =>
    $isHidden &&
    css`
      opacity: 0.35;

      @media print {
        display: none !important;
      }
    `}

  &:hover > button.row-toggle {
    opacity: 0.6;
  }
`

export const RowToggleButton = styled.button<{ $isHidden?: boolean }>`
  position: absolute;
  left: -22px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 0)};
  transition: opacity 0.2s ease;
  padding: 0;
  color: ${cardisTheme.colors.secondary};

  &:hover {
    opacity: 1 !important;
  }

  @media print {
    display: none !important;
  }
`

export const TechniqueRow = styled.div<{ $isTotal?: boolean }>`
  display: grid;
  grid-template-columns: 160px 1fr 40px 96px;
  gap: 6px;
  padding: 6px 0;
  border-bottom: 1px solid
    ${({ $isTotal }) =>
      $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.borderLight};
  align-items: baseline;

  ${({ $isTotal }) =>
    $isTotal &&
    css`
      padding-top: 6px;
      border-top: 2px solid ${cardisTheme.colors.secondary};
      border-bottom: none;
    `}
`

export const TechniqueLabel = styled.span<{ $isTotal?: boolean }>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '13px' : '12px')};
  font-weight: ${({ $isTotal }) => ($isTotal ? 500 : 400)};
  color: ${({ $isTotal }) =>
    $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.primary};

  @media print {
    ${printStyles}
  }
`

export const TechniqueDetails = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

export const TechniqueCurrency = styled.span<{ $isTotal?: boolean }>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '13px' : '12px')};
  font-weight: 400;
  color: ${({ $isTotal }) =>
    $isTotal ? cardisTheme.colors.secondary : cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

export const TechniqueValue = styled.span<{
  $isTotal?: boolean
  $isNegative?: boolean
}>`
  font-family: ${cardisTheme.fonts.body};
  font-size: ${({ $isTotal }) => ($isTotal ? '13px' : '12px')};
  font-weight: ${({ $isTotal }) => ($isTotal ? 600 : 400)};
  color: ${({ $isTotal, $isNegative }) =>
    $isTotal
      ? cardisTheme.colors.secondary
      : $isNegative
        ? cardisTheme.colors.textLight
        : cardisTheme.colors.textLight};
  text-align: right;

  @media print {
    ${printStyles}
  }
`

// Custom row wrapper with remove button
export const CustomRowWrapper = styled.div<{
  $isDragging?: boolean
  $isDragOver?: boolean
}>`
  position: relative;

  &:hover > button.remove-row,
  &:hover > button.drag-handle {
    opacity: 0.6;
  }

  &:hover .row-mode-toggle {
    opacity: 0.75;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      opacity: 0.4;
    `}

  ${({ $isDragOver }) =>
    $isDragOver &&
    css`
      box-shadow: inset 0 2px 0 0 ${cardisTheme.colors.secondary};
    `}

  @media print {
    box-shadow: none !important;
  }
`

// Drag handle (6-dot grip) for reordering custom rows. Sits left of the
// remove (-42px) and hide (-22px) controls so the three never overlap.
export const DragHandle = styled.button`
  position: absolute;
  left: -62px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 0;
  color: ${cardisTheme.colors.textLight};

  &:hover {
    opacity: 1 !important;
    color: ${cardisTheme.colors.secondary};
  }

  &:active {
    cursor: grabbing;
  }

  @media print {
    display: none !important;
  }
`

// Details cell for custom rows: flex so the flat/calc mode toggle can sit at
// the left while the amount (or quantity × price) stays right-aligned.
export const CustomDetails = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;

  @media print {
    ${printStyles}
  }
`

// Right-aligned content holder (the CHF amount or the quantity × price expression)
export const CustomDetailsValue = styled.span`
  text-align: right;
`

// Two-segment flat / calc mode toggle, revealed on row hover.
export const RowModeToggle = styled.div`
  display: inline-flex;
  align-self: center;
  margin-right: auto; /* push to the left edge of the details cell */
  border: 1px solid ${cardisTheme.colors.border};
  border-radius: 10px;
  overflow: hidden;
  font-size: 8px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 1 !important;
  }

  & > span {
    padding: 2px 6px;
    color: ${cardisTheme.colors.textLight};
    background: ${cardisTheme.colors.white};
    white-space: nowrap;
  }
  & > span.active {
    background: ${cardisTheme.colors.secondary};
    color: ${cardisTheme.colors.white};
  }

  @media print {
    display: none !important;
  }
`

// m² / m³ unit token inside a calc row — clickable to toggle the unit.
export const UnitToggle = styled.button`
  border: none;
  background: none;
  padding: 0 1px;
  font: inherit;
  color: ${cardisTheme.colors.secondary};
  border-bottom: 1px dotted ${cardisTheme.colors.secondary};
  cursor: pointer;

  @media print {
    color: ${cardisTheme.colors.textLight};
    border-bottom: none;
    cursor: default;
  }
`

export const RemoveRowButton = styled.button`
  position: absolute;
  /* Sits just left of the shared row-toggle (eye) at -22px so the hide and
     remove controls don't stack on top of each other on custom rows. */
  left: -42px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: ${cardisTheme.colors.secondary};
  color: white;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  padding: 0;

  &:hover {
    opacity: 1 !important;
    background: #c0392b;
  }

  @media print {
    display: none !important;
  }
`

export const AddRowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  margin-top: 4px;
  border: 1px dashed ${cardisTheme.colors.borderLight};
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  color: ${cardisTheme.colors.textLight};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${cardisTheme.colors.secondary};
    color: ${cardisTheme.colors.secondary};
  }

  @media print {
    display: none !important;
  }
`

// ESTIMATION HÉDONISTE - Horizontal layout
export const HedonicSection = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  gap: 26px;
  align-items: flex-start;
  margin-top: 6px;

  @media print {
    display: grid !important;
    grid-template-columns: 3fr 1fr 1fr !important;
    gap: 26px !important;
    margin-top: 6px !important;
  }
`

export const HedonicMainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 3px !important;
  }
`

export const HedonicMainValue = styled.div`
  font-family: ${cardisTheme.fonts.body};
  font-size: 20px;
  font-weight: 600;
  color: ${cardisTheme.colors.secondary};
  line-height: 1.1;

  @media print {
    ${printStyles}
  }
`

export const HedonicPricePerSqm = styled.div`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

export const HedonicRangeColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 3px !important;
  }
`

export const HedonicRangeLabel = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

export const HedonicRangeValue = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 12px;
  font-weight: 600;
  color: ${cardisTheme.colors.secondary};

  @media print {
    ${printStyles}
  }
`

export const HedonicRangePricePerSqm = styled.span`
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  font-weight: 400;
  color: ${cardisTheme.colors.textLight};

  @media print {
    font-size: 10px !important;
    color: ${cardisTheme.colors.textLight} !important;
    ${printStyles}
  }
`

// Section header row with toggle icon
export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 0 10px 0;
`

export const ToggleIconButton = styled.button<{ $isHidden?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 0.6)};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  @media print {
    display: none !important;
  }
`

// Wrapper for hedonic section that can be hidden on print
export const HedonicBlock = styled.div<{ $isHidden?: boolean }>`
  ${({ $isHidden }) =>
    $isHidden &&
    css`
      @media print {
        display: none !important;
      }
    `}
`

// AVM toggle chips row
export const AvmChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  position: relative;
  z-index: 1;

  @media print {
    display: none !important;
  }
`

export const AvmChip = styled.button<{ $color: string; $isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid ${({ $color, $isHidden }) => ($isHidden ? '#ccc' : $color)};
  border-radius: 10px;
  background: ${({ $isHidden }) => ($isHidden ? '#f5f5f5' : 'white')};
  cursor: pointer;
  font-family: ${cardisTheme.fonts.body};
  font-size: 10px;
  color: ${({ $color, $isHidden }) => ($isHidden ? '#999' : $color)};
  opacity: ${({ $isHidden }) => ($isHidden ? 0.6 : 1)};
  transition: all 0.2s ease;
  text-decoration: ${({ $isHidden }) => ($isHidden ? 'line-through' : 'none')};

  &:hover {
    opacity: 1;
    background: ${({ $color, $isHidden }) =>
      $isHidden ? '#eee' : `${$color}10`};
  }
`

export const AvmChipDot = styled.span<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

// Chart section
export const ChartSection = styled.div`
  margin-top: -16px;
`

// Property images section
export const ImagesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 13px;
  margin-top: 10px;
  padding: 0 20px;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 13px !important;
    margin-top: 10px !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }
`

export const TechniqueImageRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 13px;

  @media print {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 13px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
`

export const PropertyImage = styled.img`
  width: 100%;
  height: 190px;
  object-fit: cover;
  border-radius: 0;

  @media print {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    ${printStyles}
  }
`

export const ImagePlaceholder = styled.div`
  width: 100%;
  height: 190px;
  background: ${cardisTheme.colors.backgroundGray};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${cardisTheme.colors.textLight};
  font-size: 10px;

  @media print {
    display: none !important;
  }
`

export const TechniqueFooterWrapper = styled(SharedFooterWrapper)`
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  @media print {
    min-height: 160px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
`

export const TechniqueFinalBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  @media print {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
`
